import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, Sparkles } from "lucide-react";

/**
 * ProgressiveChecklist — one-question-at-a-time full-screen modal.
 *
 * Reusable engine for: instructor daily/weekly checks, learner prep, PDI mock prep,
 * end-of-lesson reviews. Only the `check` prop changes.
 *
 * Props:
 *  - check:    { key, title, subtitle?, completion_title, completion_subtitle?,
 *                cta_label?, questions: [ { key, prompt, checkbox_label?, kind?, placeholder? } ] }
 *  - onClose:  () => void                  — fires on dismiss / "X" / esc
 *  - onComplete: ({ skipped, answers }) => void  — fires after submit
 *  - onCta:    () => void                  — fires when the completion CTA is tapped
 *  - autoCloseMs: number = 1800            — completion screen auto-close delay
 *  - extraCompletionNode: React.Node       — slot rendered between subtitle and CTA on completion
 *  - completionTitleOverride: string       — replaces the catalogue completion title
 *  - completionSubtitleOverride: string    — replaces the catalogue completion subtitle
 *
 * UX rules:
 *  - Tick → 220ms scale-in glow → auto-advance
 *  - Skip is small text, bottom of screen
 *  - Text question: textarea + Continue button (no auto-advance)
 *  - Completion screen with checkmark animation → auto-close (delayed when extras present)
 */
export default function ProgressiveChecklist({
  check,
  onClose,
  onComplete,
  onCta,
  autoCloseMs = 1800,
  extraCompletionNode = null,
  completionTitleOverride = null,
  completionSubtitleOverride = null,
}) {
  const questions = check?.questions || [];
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("question"); // question | celebrating | done
  const [textDraft, setTextDraft] = useState("");
  const submittedRef = useRef(false);

  const total = questions.length;
  const q = questions[idx];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const advance = (newAnswers) => {
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setTextDraft("");
    } else {
      finalise(newAnswers, false);
    }
  };

  const finalise = (finalAnswers, skipped) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("done");
    onComplete?.({ skipped, answers: finalAnswers });
    // When the completion screen carries extras (e.g. "leave in 22 mins"),
    // give the user time to read it before auto-closing.
    const hold = extraCompletionNode ? Math.max(autoCloseMs, 4500) : autoCloseMs;
    setTimeout(() => {
      onClose?.();
    }, hold + 800);
  };

  const tick = () => {
    if (!q) return;
    const next = { ...answers, [q.key]: true };
    setAnswers(next);
    setPhase("celebrating");
    setTimeout(() => {
      setPhase("question");
      advance(next);
    }, 240);
  };

  const skip = () => {
    finalise(answers, true);
  };

  const submitText = () => {
    if (!q) return;
    const v = (textDraft || "").trim();
    if (!v) return;
    const next = { ...answers, [q.key]: v };
    setAnswers(next);
    advance(next);
  };

  const isText = q?.kind === "text";
  const progress = useMemo(() => {
    if (phase === "done") return total;
    return Math.min(idx, total);
  }, [idx, phase, total]);

  return (
    <div
      data-testid="progressive-checklist"
      data-check-key={check?.key}
      className="fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-sm flex flex-col"
    >
      <header className="px-4 py-3 flex items-center gap-3 border-b border-slate-800/60">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-400">
            {check?.title || "Check"}
          </p>
          {check?.subtitle && (
            <p className="text-[11px] text-slate-400 truncate">{check.subtitle}</p>
          )}
        </div>
        <div data-testid="checklist-progress" className="text-[11px] font-extrabold text-slate-300 tabular-nums">
          {progress + (phase === "done" ? 0 : 1)} / {total}
        </div>
        <button
          data-testid="checklist-close"
          onClick={onClose}
          aria-label="Close"
          className="p-2 rounded-xl hover:bg-slate-800 text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </header>

      {/* progress bar */}
      <div className="h-1 bg-slate-800">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
          style={{ width: `${(progress / Math.max(1, total)) * 100}%` }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center px-5">
        {phase === "done" ? (
          <CompletionView
            check={check}
            extraCompletionNode={extraCompletionNode}
            completionTitleOverride={completionTitleOverride}
            completionSubtitleOverride={completionSubtitleOverride}
            onCta={() => {
              onCta?.();
              onClose?.();
            }}
          />
        ) : isText ? (
          <TextQuestionView
            q={q}
            value={textDraft}
            onChange={setTextDraft}
            onSubmit={submitText}
            onSkip={skip}
          />
        ) : (
          <CheckboxQuestionView
            q={q}
            celebrating={phase === "celebrating"}
            onTick={tick}
            onSkip={skip}
          />
        )}
      </div>
    </div>
  );
}

function CheckboxQuestionView({ q, celebrating, onTick, onSkip }) {
  return (
    <div className="w-full max-w-md">
      <h2
        data-testid="checklist-question"
        className="text-2xl sm:text-3xl font-extrabold text-white text-center leading-tight mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500"
      >
        {q?.prompt}
      </h2>
      <button
        data-testid="checklist-tick"
        onClick={onTick}
        className={`w-full py-5 rounded-2xl font-extrabold text-base sm:text-lg inline-flex items-center justify-center gap-3 transition-all duration-200 ${
          celebrating
            ? "bg-emerald-500 text-white scale-[1.02] shadow-2xl shadow-emerald-500/40"
            : "bg-white text-slate-900 hover:bg-emerald-50 hover:shadow-xl"
        }`}
      >
        <span
          className={`w-9 h-9 rounded-xl inline-flex items-center justify-center transition-colors ${
            celebrating ? "bg-white/30" : "bg-emerald-500/15"
          }`}
        >
          <Check
            className={`w-5 h-5 ${
              celebrating ? "text-white" : "text-emerald-600"
            }`}
            strokeWidth={3}
          />
        </span>
        {q?.checkbox_label || "Yes"}
      </button>
      <button
        data-testid="checklist-skip"
        onClick={onSkip}
        className="w-full mt-5 text-center text-[12px] text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline"
      >
        Skip
      </button>
    </div>
  );
}

function TextQuestionView({ q, value, onChange, onSubmit, onSkip }) {
  return (
    <div className="w-full max-w-md">
      <h2
        data-testid="checklist-question"
        className="text-2xl sm:text-3xl font-extrabold text-white text-center leading-tight mb-6 animate-in fade-in slide-in-from-bottom-2 duration-500"
      >
        {q?.prompt}
      </h2>
      <textarea
        data-testid="checklist-text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        autoFocus
        placeholder={q?.placeholder || "Type your answer…"}
        className="w-full px-4 py-3 rounded-2xl bg-slate-900 border border-slate-700 focus:border-violet-500 text-white text-base placeholder:text-slate-500 outline-none"
      />
      <button
        data-testid="checklist-text-submit"
        onClick={onSubmit}
        disabled={!(value || "").trim()}
        className="w-full mt-4 py-4 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-extrabold text-base inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Continue
      </button>
      <button
        data-testid="checklist-skip"
        onClick={onSkip}
        className="w-full mt-3 text-center text-[12px] text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline"
      >
        Skip
      </button>
    </div>
  );
}

function CompletionView({
  check,
  onCta,
  extraCompletionNode = null,
  completionTitleOverride = null,
  completionSubtitleOverride = null,
}) {
  const title = completionTitleOverride || check?.completion_title || "Done";
  const subtitle =
    completionSubtitleOverride !== null
      ? completionSubtitleOverride
      : check?.completion_subtitle;
  return (
    <div data-testid="checklist-completion" className="w-full max-w-md text-center animate-in fade-in zoom-in-95 duration-500">
      <div className="relative mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-2xl shadow-emerald-500/40 animate-in zoom-in-50 duration-500">
        <Check className="w-12 h-12 text-white" strokeWidth={3} />
        <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-amber-300 animate-pulse" />
      </div>
      <h1 data-testid="checklist-completion-title" className="mt-6 text-2xl sm:text-3xl font-extrabold text-white">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-2 text-[13px] text-slate-400">
          {subtitle}
        </p>
      )}
      {extraCompletionNode && (
        <div data-testid="checklist-completion-extras" className="mt-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
          {extraCompletionNode}
        </div>
      )}
      {check?.cta_label && (
        <button
          data-testid="checklist-cta"
          onClick={onCta}
          className="mt-7 px-6 py-3 rounded-2xl bg-white text-slate-900 font-extrabold text-sm hover:bg-slate-100"
        >
          {check.cta_label}
        </button>
      )}
    </div>
  );
}
